/* ------------------------------------------------------------------
   HAUS Terrazzo journey map.

   Non-negotiable #9 compliance: there is no scroll listener in this file.
   Which stop is active is decided entirely by an IntersectionObserver
   watching one invisible step per stop, with the root collapsed to a line
   at the viewport midline (rootMargin -50% top and bottom).

   requestAnimationFrame is used only to tween the comet between two fixed
   points for ~700ms after the observer has already decided where it is
   going. It is not sampled against scroll position and it stops as soon as
   the tween finishes, so scroll performance is untouched.

   Idempotent: guards on window.HausTerrazzoJourney, and re-inits per
   section on shopify:section:load so the theme editor stays live.

   Below 768px, updateMobileFocus applies a CSS transform (transition
   handled entirely in CSS, no rAF) to pan and zoom the same SVG so each
   stop's real coordinates land under a fixed centre dot, instead of
   shrinking the whole map and moving a dot across it. It only runs from
   setActive (already scroll-observer-driven) and from a resize listener,
   so it does not add a scroll listener either. Desktop is unaffected:
   the CSS gating this is itself scoped to max-width: 767.98px.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  if (window.HausTerrazzoJourney) return;
  window.HausTerrazzoJourney = true;

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var TWEEN_MS = 700;
  var TRAIL_LEN = 8;
  var MOBILE_ZOOM = 2.6;
  var MOBILE_FINALE_ZOOM = 1.5;
  var mobileMql = window.matchMedia('(max-width: 767.98px)');

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function el(name, attrs) {
    var node = document.createElementNS(SVG_NS, name);
    for (var k in attrs) {
      if (Object.prototype.hasOwnProperty.call(attrs, k)) {
        node.setAttribute(k, attrs[k]);
      }
    }
    return node;
  }

  function parseJSON(node, fallback) {
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return fallback;
    }
  }

  /* Quadratic arc between two stops, lifted perpendicular to the chord so
     legs bow away from the equator rather than cutting straight through. */
  function control(from, to) {
    var mx = (from.x + to.x) / 2;
    var my = (from.y + to.y) / 2;
    var dist = Math.hypot(to.x - from.x, to.y - from.y);
    return { x: mx, y: my - Math.min(dist * 0.22, 90) };
  }

  function pointAt(from, ctrl, to, t) {
    var u = 1 - t;
    return {
      x: u * u * from.x + 2 * u * t * ctrl.x + t * t * to.x,
      y: u * u * from.y + 2 * u * t * ctrl.y + t * t * to.y
    };
  }

  function easeInOut(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  function initOne(root) {
    if (!root || root.dataset.tjInit === '1') return;
    root.dataset.tjInit = '1';

    var section = root.closest('.haus-terrazzo-journey');
    var stopNodes = Array.prototype.slice.call(root.querySelectorAll('[data-tj-stop]'));
    if (!stopNodes.length) return;

    var stops = stopNodes.map(function (node) {
      return {
        node: node,
        x: parseFloat(node.getAttribute('data-x')),
        y: parseFloat(node.getAttribute('data-y'))
      };
    });

    var copy = parseJSON(root.querySelector('[data-tj-copy]'), []);
    var cities = parseJSON(root.querySelector('[data-tj-cities]'), []);
    var finaleCopy = parseJSON(root.querySelector('[data-tj-finale-copy]'), null);

    var routesG = root.querySelector('[data-tj-routes]');
    var finaleG = root.querySelector('[data-tj-finale]');
    var trailG = root.querySelector('[data-tj-trail]');
    var comet = root.querySelector('[data-tj-comet]');
    var locEl = root.querySelector('[data-tj-loc]');
    var txtEl = root.querySelector('[data-tj-txt]');
    var destsEl = root.querySelector('[data-tj-dests]');

    /* ---- build the legs ---- */
    var legs = [];
    for (var i = 0; i < stops.length - 1; i++) {
      var from = stops[i];
      var to = stops[i + 1];
      var c = control(from, to);
      var path = el('path', {
        class: 'haus-tj__leg',
        d: 'M' + from.x + ',' + from.y + ' Q' + c.x + ',' + c.y + ' ' + to.x + ',' + to.y
      });
      routesG.appendChild(path);
      legs.push({ node: path, from: from, to: to, ctrl: c });
    }

    /* ---- build the finale rays and city chips ---- */
    var rays = [];
    var chips = [];
    if (finaleG && cities.length) {
      var last = stops[stops.length - 1];
      cities.forEach(function (city) {
        var ray = el('path', {
          class: 'haus-tj__ray',
          d: 'M' + last.x + ',' + last.y + ' L' + city.x + ',' + city.y
        });
        finaleG.appendChild(ray);
        rays.push(ray);

        var g = el('g', { class: 'haus-tj__chip' });
        g.appendChild(el('circle', { cx: city.x, cy: city.y, r: 4 }));
        finaleG.appendChild(g);
        chips.push(g);
      });
    }

    /* ---- trail pool ---- */
    var trail = [];
    if (trailG) {
      for (var s = 0; s < TRAIL_LEN; s++) {
        var spark = el('circle', { class: 'haus-tj__spark', r: 0, cx: 0, cy: 0 });
        trailG.appendChild(spark);
        trail.push(spark);
      }
    }

    function clearTrail() {
      trail.forEach(function (node) { node.setAttribute('r', 0); });
    }

    var current = 0;
    var rafId = null;

    function paintCaption(index) {
      var isFinale = index >= stops.length;
      var entry = isFinale ? finaleCopy : copy[index];
      if (!entry) return;
      if (locEl) locEl.textContent = entry.title || '';
      if (txtEl) txtEl.textContent = entry.caption || '';
    }

    function paintStops(index) {
      var isFinale = index >= stops.length;
      stops.forEach(function (stop, i) {
        // After the finale nothing is "current", so no pulse survives the end.
        var passed = i < index || (isFinale && i === stops.length - 1);
        var active = !isFinale && i === index;
        stop.node.classList.toggle('is-passed', passed);
        stop.node.classList.toggle('is-active', active);
      });
      legs.forEach(function (leg, i) {
        leg.node.classList.toggle('is-shown', i < index);
      });
      var showFinale = isFinale;
      rays.forEach(function (r) { r.classList.toggle('is-shown', showFinale); });
      chips.forEach(function (c) { c.classList.toggle('is-shown', showFinale); });
      if (destsEl) destsEl.classList.toggle('is-shown', showFinale);
    }

    function flyTo(fromIndex, toIndex) {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }

      var goingForward = toIndex > fromIndex;
      var legIndex = goingForward ? fromIndex : toIndex;
      var leg = legs[legIndex];

      if (!comet || !leg || reduced()) {
        if (comet) comet.classList.remove('is-flying');
        clearTrail();
        return;
      }

      var from = goingForward ? leg.from : leg.to;
      var to = goingForward ? leg.to : leg.from;
      var ctrl = leg.ctrl;
      var start = null;

      comet.classList.add('is-flying');

      function frame(ts) {
        if (start === null) start = ts;
        var raw = Math.min(1, (ts - start) / TWEEN_MS);
        var t = easeInOut(raw);
        var p = pointAt(from, ctrl, to, t);
        comet.setAttribute('cx', p.x);
        comet.setAttribute('cy', p.y);

        for (var k = 0; k < trail.length; k++) {
          var lag = t - (k + 1) * 0.045;
          if (lag <= 0) { trail[k].setAttribute('r', 0); continue; }
          var q = pointAt(from, ctrl, to, lag);
          trail[k].setAttribute('cx', q.x);
          trail[k].setAttribute('cy', q.y);
          trail[k].setAttribute('r', Math.max(0, 5 - k * 0.55));
          trail[k].setAttribute('opacity', Math.max(0, 0.45 - k * 0.05));
        }

        if (raw < 1) {
          rafId = requestAnimationFrame(frame);
        } else {
          rafId = null;
          comet.classList.remove('is-flying');
          clearTrail();
        }
      }

      rafId = requestAnimationFrame(frame);
    }

    /* ---- mobile: pan the map to keep the fixed centre dot on the real
       stop, instead of moving a dot across a shrunk-down full map. Only
       runs the transform below 768px; the box/dot CSS itself is also
       gated to that width, so this is a no-op at desktop widths beyond
       the cost of one matchMedia check. Not a scroll listener: it only
       runs from setActive (already driven by the IntersectionObserver)
       and from resize. */
    var mapBox = root.querySelector('.haus-tj__box');
    var mapSvg = root.querySelector('.haus-mapbox__w');

    function updateMobileFocus(index) {
      if (!mobileMql.matches || !mapBox || !mapSvg || !stops.length) return;
      var boxRect = mapBox.getBoundingClientRect();
      if (!boxRect.width) return;

      var isFinale = index >= stops.length;
      var target = isFinale ? stops[stops.length - 1] : stops[index];
      if (!target) return;

      var zoom = isFinale ? MOBILE_FINALE_ZOOM : MOBILE_ZOOM;
      // The svg is width:100% of the box and scales uniformly, so one svg
      // unit is boxWidth/1000 in both axes. Centre on the box's own width
      // and height separately: the box is 4/5, not square, so reusing
      // width for the vertical term would park the dot above centre.
      var unitPx = boxRect.width / 1000;
      var tx = boxRect.width / 2 - target.x * unitPx * zoom;
      var ty = boxRect.height / 2 - target.y * unitPx * zoom;

      mapSvg.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + zoom + ')';
    }

    var mobileResizeRaf = null;
    function scheduleMobileResize() {
      if (mobileResizeRaf) return;
      mobileResizeRaf = requestAnimationFrame(function () {
        mobileResizeRaf = null;
        updateMobileFocus(current);
      });
    }
    window.addEventListener('resize', scheduleMobileResize);

    function setActive(index) {
      if (index === current) return;
      var previous = current;
      current = index;
      paintStops(index);
      paintCaption(index);
      updateMobileFocus(index);

      // The finale is a reveal, not a flight: the comet has already landed.
      var maxStop = stops.length - 1;
      if (previous <= maxStop && index <= maxStop) {
        flyTo(previous, index);
      } else if (comet) {
        comet.classList.remove('is-flying');
        clearTrail();
      }
    }

    /* ---- observer: the only thing that reads scroll position ---- */
    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-tj-step]'));

    if (!('IntersectionObserver' in window) || !steps.length) {
      // No observer support: show the completed state rather than a frozen
      // first frame, so the section still communicates the whole journey.
      paintStops(stops.length);
      paintCaption(stops.length);
      updateMobileFocus(stops.length);
      if (section) section.classList.add('haus-tj-ready');
      return;
    }

    /* The band is deliberately -45%/-45% rather than the -50%/-50% the
       scrollytelling pattern usually reaches for. Collapsing the root to a
       zero-height line makes every intersection zero-area, and an engine
       that compares that against threshold 0 can decide nothing ever
       intersects. Leaving a 10% tall band guarantees real overlap and costs
       nothing: steps are far taller than the band, so exactly one still
       occupies it at a time. */
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var idx = parseInt(entry.target.getAttribute('data-tj-step'), 10);
        if (!isNaN(idx)) setActive(idx);
      });
    }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });

    steps.forEach(function (step) { observer.observe(step); });

    if (section) section.classList.add('haus-tj-ready');
    paintStops(0);
    paintCaption(0);
    updateMobileFocus(0);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-haus-tj]');
    Array.prototype.forEach.call(roots, initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
